import {noop} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {StudyDesignType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import usePlateMapDisplayAttributeState from 'samples/plate-maps/hooks/usePlateMapDisplayAttributeState';
import {PlateRecord, PlateTransferType} from 'samples/plate-maps/plate-record/PlateRecordConstants';
import {
  DisplayLayerMetadata,
  DisplayLayerOptionSection,
  PlateMap,
  ReagentAssignmentGroup,
  REAGENTS_ATTRIBUTE_SENTINEL,
  SchematizedAttribute,
  WELL_ROLE_ATTRIBUTE_SENTINEL,
} from 'samples/plate-maps/PlateMapConstants';
import PlateMapPane from 'samples/plate-maps/PlateMapPane';
import {
  getReadonlyConcentrationLayersProps,
  getReadonlyMetadataLayersProps,
  getReadonlyContentsLayersProps,
  getDisplayLayerAttributeKey,
  shouldShowTestOrderDilutionInfo,
} from 'samples/plate-maps/PlateMapUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {readableTime} from 'util/DateUtil';

import {PlateMapSidebar} from './sidebar/PlateMapSidebar';

export interface PlateMapSummaryProps {
  plateMap: PlateMap;
  plateDataParametersOptions: Array<DisplayLayerMetadata>;
  wellSchemaOptions: Array<DisplayLayerMetadata>;
  frozenReagentAssignmentGroups: {[id: string]: ReagentAssignmentGroup} | null;
  /* Design type of the associated procedure's study. This controls showing test order/dilution related
  information only for sample testing studies. */
  procedureStudyDesignType: StudyDesignType | null;
  plateMapType: PlateMapType;
  additionalMetadataContent: JSX.Element;
  plateRecord?: PlateRecord;
  disabledSections?: Array<DisplayLayerOptionSection>;
  hiddenSections?: Array<DisplayLayerOptionSection>;
  isDisplayingScratchPlate?: boolean;
}

export default function PlateMapSummary({
  plateMap,
  plateDataParametersOptions,
  wellSchemaOptions,
  frozenReagentAssignmentGroups,
  procedureStudyDesignType,
  plateMapType,
  additionalMetadataContent,
  plateRecord,
  disabledSections,
  hiddenSections,
  isDisplayingScratchPlate,
}: PlateMapSummaryProps): JSX.Element {
  // Set selectedAttribute to well roles if the contents section is disabled or hidden since
  // REAGENTS_ATTRIBUTE_SENTINEL is part of the contents section.
  const initialSelectedAttribute = useMemo(() => {
    if (
      disabledSections?.includes(DisplayLayerOptionSection.CONTENTS) ||
      hiddenSections?.includes(DisplayLayerOptionSection.CONTENTS)
    ) {
      return WELL_ROLE_ATTRIBUTE_SENTINEL;
    }
    return REAGENTS_ATTRIBUTE_SENTINEL;
  }, [disabledSections, hiddenSections]);

  const {assignmentGroupsPerAttribute, selectedAttribute, setSelectedAttribute, displayLayer} =
    usePlateMapDisplayAttributeState(
      plateMap,
      initialSelectedAttribute,
      false,
      frozenReagentAssignmentGroups == null
        ? undefined
        : {[REAGENTS_ATTRIBUTE_SENTINEL]: frozenReagentAssignmentGroups}
    );

  const allHiddenSections = useMemo(
    () => [
      ...(!shouldShowTestOrderDilutionInfo(procedureStudyDesignType, plateMapType)
        ? [DisplayLayerOptionSection.TEST_METADATA]
        : []),
      ...(hiddenSections ?? []),
    ],
    [procedureStudyDesignType, plateMapType, hiddenSections]
  );

  const isRecord = plateMapType === PlateMapType.RECORD;
  const isDesign = plateMapType === PlateMapType.DESIGN;

  const action = plateRecord?.transferType === PlateTransferType.UPDATE ? 'updated' : 'created';

  // Set up props for PlateMapDisplayLayerSidebar
  const groupedValidationErrors = {};
  const memoizedDisabledSections = useMemo(() => disabledSections ?? [], [disabledSections]);
  const wellSchemaLayersProps = useMemo(
    () => ({
      enableAddRemoveLayers: false,
      availableLayers: wellSchemaOptions,
      selectedLayerIds: new Set(
        wellSchemaOptions.map((layer) => (layer.attribute as SchematizedAttribute).key)
      ),
      onToggleLayer: noop,
      onRemoveLayer: noop,
    }),
    [wellSchemaOptions]
  );
  const parameterLayersProps = useMemo(
    () => ({
      enableAddRemoveLayers: false,
      availableLayers: plateDataParametersOptions,
      selectedLayerIds: new Set(
        plateDataParametersOptions.map((layer) => (layer.attribute as SchematizedAttribute).key)
      ),
      onToggleLayer: noop,
      onRemoveLayer: noop,
    }),
    [plateDataParametersOptions]
  );
  const concentrationLayersProps = useMemo(() => getReadonlyConcentrationLayersProps(plateMap), [plateMap]);
  const metadataLayersProps = useMemo(() => getReadonlyMetadataLayersProps(plateMap), [plateMap]);
  const contentsLayersProps = useMemo(() => getReadonlyContentsLayersProps(plateMap), [plateMap]);

  // Set up props for PlateMapPane
  const disabledPlateMapPaneProps = {
    selectedWellIndices: new Set<number>(),
    setSelectedWellIndices: noop,
    previewedWellIndices: new Set<number>(),
    deselectWellsOnClickRefs: [],
  };

  return (
    <>
      <PlateMapSidebar
        plateMapType={plateMapType}
        plateMap={plateMap}
        selectedAttributeKey={getDisplayLayerAttributeKey(selectedAttribute)}
        setSelectedAttribute={setSelectedAttribute}
        assignmentGroups={assignmentGroupsPerAttribute}
        disabledSections={memoizedDisabledSections}
        hiddenSections={allHiddenSections}
        groupedValidationErrors={groupedValidationErrors}
        wellSchemaLayersProps={wellSchemaLayersProps}
        parameterLayersProps={parameterLayersProps}
        additionalContent={additionalMetadataContent}
        plateRecord={plateRecord}
        concentrationLayersProps={concentrationLayersProps}
        metadataLayersProps={metadataLayersProps}
        contentsLayersProps={contentsLayersProps}
        isDisplayingScratchPlate={isDisplayingScratchPlate}
      />
      <div className={css(styles.border)} />
      <div className={css(styles.plateMap)}>
        <PlateMapPane
          {...disabledPlateMapPaneProps}
          header={
            <div className={css(styles.plateMapPaneInfo)}>
              {isRecord && plateRecord != null && (
                <>
                  <div
                    {...getTestProps({component: 'PlateRecordSummary', key: 'plateActionInfo'})}
                    className={css(styles.recordChip)}
                  >
                    <strong>Plate </strong>
                    <BenchChip
                      className={css(styles.plateChip)}
                      id={plateRecord.plateApiId}
                      size={ChipSize.TABLE_CELL}
                    />
                    <strong> {action} on </strong>
                    {readableTime(plateRecord.createdAt)}
                  </div>
                </>
              )}
              {isDesign && (
                <>
                  <span />
                  <span>{/* TODO(BNCH-112173): Potentially add the created at date. */}</span>
                </>
              )}
            </div>
          }
          plateMapDisplayParams={{
            plateMap,
            displayLayer,
            attribute: selectedAttribute,
            assignmentGroupsById:
              assignmentGroupsPerAttribute[getDisplayLayerAttributeKey(selectedAttribute)] ?? {},
            groupedValidationErrors,
          }}
          isPlateMapViewOnly={true}
          showFinalMeasurements={true}
          maxPlateViewHeight="500px"
          plateMapType={plateMapType}
        />
      </div>
    </>
  );
}

const styles = StyleSheet.create({
  border: {
    flex: '0 0 0px',
    borderRight: `1px solid ${Color.GRAY_300}`,
    margin: '-16px 16px -16px 0',
  },
  plateMap: {
    flex: 1,
    overflow: 'auto',
  },
  recordChip: {
    verticalAlign: 'center',
    display: 'flex',
    justifyContent: 'flex-start',
    gap: 4,
    alignItems: 'center',
  },
  plateChip: {
    marginRight: 0,
  },
  plateMapPaneInfo: {
    height: '24px',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
});
